import React from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { SectionFrame } from '@/components/layout/SectionFrame';
import { useLanguage } from '@/contexts/LanguageContext';

// Internal constants per spec
const BG_URL = '/op-uploads/DSC_5226.webp'; // must be used literally
const CARD_IMG = '/op-uploads/2025/09/image/1759007030691-06069a2c-2c99-4b7d-af4e-1ca09a21f2b7.jpg';

const overlayGradient = 'linear-gradient(180deg, rgba(11,17,27,.78) 0%, rgba(11,17,27,.64) 40%, rgba(11,17,27,.50) 100%)';

export const InvestmentPhilosophy: React.FC<{ sectionIndex?: number }>= ({ sectionIndex = 0 }) => {
  const { language } = useLanguage();
  const reduceMotion = useReducedMotion();
  const isRTL = language === 'ar';

  // Texts (match keys/content exactly)
  const texts = React.useMemo(() => {
    if (language === 'ar') {
      return {
        kicker: 'فلسفتنا الاستثمارية',
        titleHtml: 'نصنع <span class="accent">القيمة</span> بمنهجٍ لا يُساوم',
        body: 'نبحث قبل أن نستثمر، ونقيس قبل أن نَعِد. نُميّز الفرص، نختبر الفرضيات، ثم ننفّذ بانضباط يوازن العائد والمخاطر. بهذه الطريقة تتحوّل الرؤية إلى أصول، وتتحوّل الأصول إلى أثرٍ يُرى.',
        pillars: [
          'بحث مُسبق: بيانات أعمق، قرارات أوضح.',
          'تنفيذ منضبط: جودة، موعد، وتكلفة تحت السيطرة.',
          'أثر قابل للقياس: قيمة تدوم وتكبر.'
        ],
        cta: 'تعرّف على نهجنا',
        imgAlt: 'نقاش استثماري ضمن فريق العمل',
      };
    }
    return {
      kicker: 'Our Investment Philosophy',
      titleHtml: '<span class="accent">Value</span>, Crafted with Discipline',
      body: "We research before we invest—and measure before we promise. We qualify opportunities, validate assumptions, then execute with rigor to balance returns and risk. That’s how vision becomes assets—and assets become visible impact.",
      pillars: [
        'Research-First: Deeper data, clearer decisions.',
        'Disciplined Delivery: Quality, timeline, and cost in check.',
        'Measurable Impact: Value that lasts and scales.'
      ],
      cta: 'Explore Our Approach',
      imgAlt: 'Investment team discussion',
    };
  }, [language]);

  // Basic parallax state for image (disabled if reduced motion)
  const imgRef = React.useRef<HTMLDivElement | null>(null);
  const [tY, setTY] = React.useState(0);
  React.useEffect(() => {
    if (reduceMotion) return;
    let raf = 0;
    const onScroll = () => {
      if (!imgRef.current) return;
      const rect = imgRef.current.getBoundingClientRect();
      // visible ratio 0..1
      const vh = window.innerHeight || 1;
      const center = rect.top + rect.height / 2;
      const delta = (center - vh / 2) / vh; // -0.5 .. 0.5 typical
      const offset = Math.max(-10, Math.min(10, -delta * 20)); // clamp to ~ +/-10px
      setTY(offset);
    };
    const onScrollRaf = () => { raf = 0; onScroll(); };
    const handle = () => { if (!raf) raf = requestAnimationFrame(onScrollRaf); };
    window.addEventListener('scroll', handle, { passive: true });
    onScroll();
    return () => { window.removeEventListener('scroll', handle); if (raf) cancelAnimationFrame(raf); };
  }, [reduceMotion]);

  const baseDelay = 0.12;

  // Layout: dark section over fixed background
  return (
    <SectionFrame
      className="pg-section fullpage-section relative overflow-hidden"
      data-theme="dark"
      data-section-index={sectionIndex}
      center
      // Override min-height to >= 80svh
      style={{
        backgroundImage: `url('${BG_URL}')`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }}
      overlayClassName="block"
      overlayStyle={{ background: overlayGradient }}
    >
      {/* Local styles for accents */}
      <style>{`
        .accent { color: #C6A15B; }
        .accent-ice { color: #7FB3FF; }
      `}</style>

      <div className="relative z-20 w-full">
        <div className="mx-auto w-full max-w-[1200px] px-4 sm:px-6 md:px-8" style={{ paddingTop: 'var(--section-pad, 48px)', paddingBottom: 'var(--section-pad, 48px)' }}>
          <div className={`flex flex-col items-center gap-8 lg:gap-12 ${isRTL ? 'lg:flex-row-reverse' : 'lg:flex-row'}`}>
            {/* Image block */}
            <div className="w-full lg:w-[560px]">
              <div ref={imgRef} className="relative" style={{ transform: reduceMotion ? undefined : `translateY(${tY}px)`, transition: reduceMotion ? undefined : 'transform 120ms ease-out', willChange: 'transform' }}>
                <div
                  className="w-full"
                  style={{ aspectRatio: '16 / 9', borderRadius: '24px', overflow: 'hidden', boxShadow: '0 10px 24px rgba(0,0,0,.25)', border: '1px solid rgba(255,255,255,.08)' }}
                  aria-hidden
                >
                  <img src={CARD_IMG} alt={texts.imgAlt} className="h-full w-full object-cover" />
                </div>
              </div>
            </div>

            {/* Text block */}
            <div className={`w-full lg:flex-1 ${isRTL ? 'text-right' : 'text-left'}`}>
              <div className="max-w-3xl mx-auto lg:mx-0">
                {/* Eyebrow */}
                <motion.div
                  initial={{ opacity: 0, y: reduceMotion ? 0 : 8 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.4 }}
                  transition={{ duration: 0.36, ease: 'easeOut', delay: baseDelay }}
                  className="text-sm md:text-base font-medium tracking-wide text-white/80"
                >
                  {texts.kicker}
                </motion.div>

                {/* H2 */}
                <motion.h2
                  initial={{ opacity: 0, y: reduceMotion ? 0 : 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.4 }}
                  transition={{ duration: 0.44, ease: 'easeOut', delay: baseDelay + 0.08 }}
                  className={`${isRTL ? 'font-arabic-heading' : 'font-english-heading'} mt-2 text-white leading-tight`}
                  style={{ fontSize: 'clamp(24px, 3.5vw, 48px)' }}
                  aria-live="polite"
                >
                  <span dangerouslySetInnerHTML={{ __html: texts.titleHtml }} />
                </motion.h2>

                {/* Paragraph */}
                <motion.p
                  initial={{ opacity: 0, y: reduceMotion ? 0 : 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.4 }}
                  transition={{ duration: 0.44, ease: 'easeOut', delay: baseDelay + 0.16 }}
                  className={`${isRTL ? 'font-arabic-body' : 'font-english-body'} mt-4 text-white/85`}
                  style={{ fontSize: 'clamp(16px, 2.2vw, 20px)' }}
                >
                  {texts.body}
                </motion.p>

                {/* Pillars */}
                <motion.ul
                  initial={{ opacity: 0, y: reduceMotion ? 0 : 8 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.4 }}
                  transition={{ duration: 0.4, ease: 'easeOut', delay: baseDelay + 0.24 }}
                  className={`mt-6 grid gap-3 ${isRTL ? 'text-right' : 'text-left'}`}
                  aria-label={language === 'ar' ? 'الركائز الثلاث' : 'Three pillars'}
                >
                  {texts.pillars.map((pill, idx) => (
                    <li key={idx} className="text-white/85" style={{ fontSize: 'clamp(15px, 2vw, 18px)' }}>
                      • {pill}
                    </li>
                  ))}
                </motion.ul>

                {/* CTA */}
                <motion.div
                  initial={{ opacity: 0, y: reduceMotion ? 0 : 8 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.4 }}
                  transition={{ duration: 0.4, ease: 'easeOut', delay: baseDelay + 0.36 }}
                  className="mt-8"
                >
                  <button
                    onClick={() => { window.location.href = '/about'; }}
                    className="inline-flex items-center gap-2 rounded-lg px-6 py-3"
                    aria-label={texts.cta}
                    style={{
                      color: '#FFFFFF',
                      border: '1px solid rgba(255,255,255,.24)',
                      background: 'rgba(255,255,255,.06)',
                      backdropFilter: 'blur(6px)',
                    }}
                  >
                    <span>{texts.cta}</span>
                    <svg className={`w-4 h-4 transition-transform ${isRTL ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                </motion.div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </SectionFrame>
  );
};

export default InvestmentPhilosophy;
